import { useEffect, useState, type ReactNode } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { LogOut, Menu, Moon, Sun, X, type LucideIcon } from 'lucide-react';
import { useStore } from '@/store/useStore';
import { NotificationCenter } from '@/components/dashboard/NotificationCenter';
import { HeaderBrandLogo } from '@/components/branding/HeaderBrandLogo';
import { authClient } from '@/lib/auth-client';
import { clearAccessToken } from '@/services/api';
import { CloseNavContext, ScrollAreaContext } from './shellNav';

export interface ShellNavItem {
  id: string;
  label: string;
  icon: LucideIcon;
  active?: boolean;
  onSelect: () => void;
  /** Shown under the item while it is active, e.g. the dashboard's section links. */
  children?: ReactNode;
}

interface AppShellProps {
  menu: ShellNavItem[];
  /** Entries above "Log out" in the General group. */
  general?: ShellNavItem[];
  user: { name: string; email: string };
  /** Left side of the top bar, e.g. the staff client switcher. */
  topLeading?: ReactNode;
  /** Shown in the top bar when there is no leading control, e.g. the customer name. */
  context?: string;
  children: ReactNode;
}

function initials(name: string, email: string) {
  const parts = (name.trim() || email).split(/[\s@._-]+/).filter(Boolean);
  return ((parts[0]?.[0] ?? '') + (parts[1]?.[0] ?? '')).toUpperCase() || '?';
}

function handleLogout() {
  if (confirm('Are you sure you want to logout?')) {
    clearAccessToken();
    void authClient.signOut();
  }
}

function NavGroupLabel({ children }: { children: ReactNode }) {
  return (
    <p className="px-6 pb-2 text-xs font-medium uppercase tracking-wider text-gray-400 dark:text-gray-500">
      {children}
    </p>
  );
}

function NavButton({ item, onNavigate }: { item: ShellNavItem; onNavigate: () => void }) {
  const Icon = item.icon;
  return (
    <li>
      <button
        type="button"
        onClick={() => {
          item.onSelect();
          onNavigate();
        }}
        aria-current={item.active ? 'page' : undefined}
        className={`group relative flex w-full items-center gap-3 px-6 py-2.5 text-left text-[15px] ${
          item.active
            ? 'font-semibold text-gray-900 dark:text-white'
            : 'text-gray-500 hover:text-gray-900 dark:text-gray-400 dark:hover:text-white'
        }`}
      >
        {item.active && (
          <span
            aria-hidden="true"
            className="absolute left-0 top-1/2 h-8 w-1.5 -translate-y-1/2 rounded-r-full bg-primary-500"
          />
        )}
        <Icon
          size={20}
          className={
            item.active
              ? 'shrink-0 text-primary-500'
              : 'shrink-0 text-gray-400 group-hover:text-gray-600 dark:text-gray-500 dark:group-hover:text-gray-300'
          }
        />
        {item.label}
      </button>
      {item.active && item.children}
    </li>
  );
}

function ThemeNavButton() {
  const { theme, toggleTheme } = useStore();
  return (
    <NavButton
      item={{
        id: 'theme',
        label: theme === 'light' ? 'Dark mode' : 'Light mode',
        icon: theme === 'light' ? Moon : Sun,
        onSelect: toggleTheme,
      }}
      onNavigate={() => {}}
    />
  );
}

function SidebarContent({
  menu,
  general = [],
  onNavigate,
  onClose,
}: {
  menu: ShellNavItem[];
  general?: ShellNavItem[];
  onNavigate: () => void;
  onClose?: () => void;
}) {
  return (
    <>
      <div className="flex items-center justify-between gap-2 px-6 pb-8 pt-7">
        <HeaderBrandLogo size="sm" />
        {onClose && (
          <button
            type="button"
            onClick={onClose}
            aria-label="Close menu"
            className="rounded-full p-1.5 text-gray-500 hover:bg-gray-200 dark:text-gray-400 dark:hover:bg-gray-800"
          >
            <X size={18} />
          </button>
        )}
      </div>
      <nav
        aria-label="Main"
        className="flex min-h-0 flex-1 flex-col overflow-y-auto pb-6"
      >
        {menu.length > 0 && (
          <div className="mb-8">
            <NavGroupLabel>Menu</NavGroupLabel>
            <ul className="space-y-1">
              {menu.map((item) => (
                <NavButton key={item.id} item={item} onNavigate={onNavigate} />
              ))}
            </ul>
          </div>
        )}
        <div>
          <NavGroupLabel>General</NavGroupLabel>
          <ul className="space-y-1">
            {general.map((item) => (
              <NavButton key={item.id} item={item} onNavigate={onNavigate} />
            ))}
            {/* Phones have no room for the theme button in the top bar */}
            {onClose && <ThemeNavButton />}
            <NavButton
              item={{
                id: 'logout',
                label: 'Log out',
                icon: LogOut,
                onSelect: handleLogout,
              }}
              onNavigate={() => {}}
            />
          </ul>
        </div>
      </nav>
    </>
  );
}

const roundButton =
  'inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-white text-gray-700 hover:text-gray-900 dark:bg-gray-800 dark:text-gray-300 dark:hover:text-white';

/**
 * Page frame: a sidebar of modules on the left (a drawer below lg), a top bar with
 * the client switcher, notifications, theme and user, and a light panel for the page.
 */
export function AppShell({
  menu,
  general,
  user,
  topLeading,
  context,
  children,
}: AppShellProps) {
  const { theme, toggleTheme } = useStore();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [scrollArea, setScrollArea] = useState<HTMLElement | null>(null);
  const closeDrawer = () => setDrawerOpen(false);

  useEffect(() => {
    if (!drawerOpen) return;
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setDrawerOpen(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [drawerOpen]);

  return (
    <CloseNavContext.Provider value={closeDrawer}>
      <ScrollAreaContext.Provider value={scrollArea}>
        {/* Fixed frame: only the main panel scrolls, inside its own rounded corners */}
        <div className="flex h-dvh gap-3 overflow-hidden bg-white p-3 dark:bg-gray-950">
          <aside className="hidden w-64 shrink-0 flex-col rounded-[28px] bg-panel dark:bg-gray-900 lg:flex">
            <SidebarContent menu={menu} general={general} onNavigate={() => {}} />
          </aside>

          <AnimatePresence>
            {drawerOpen && (
              <>
                <motion.div
                  key="backdrop"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  onClick={closeDrawer}
                  className="fixed inset-0 z-40 bg-black/30 lg:hidden"
                />
                <motion.aside
                  key="drawer"
                  initial={{ x: '-105%' }}
                  animate={{ x: 0 }}
                  exit={{ x: '-105%' }}
                  transition={{ type: 'tween', duration: 0.2 }}
                  className="fixed inset-y-0 left-0 z-50 flex w-72 max-w-[85vw] flex-col rounded-r-[28px] bg-panel shadow-xl dark:bg-gray-900 lg:hidden"
                >
                  <SidebarContent
                    menu={menu}
                    general={general}
                    onNavigate={closeDrawer}
                    onClose={closeDrawer}
                  />
                </motion.aside>
              </>
            )}
          </AnimatePresence>

          <div className="flex min-w-0 flex-1 flex-col gap-3">
            <header className="relative z-30 flex shrink-0 items-center gap-2 rounded-[28px] bg-panel px-3 py-2.5 dark:bg-gray-900 sm:gap-3 sm:px-4">
              <button
                type="button"
                onClick={() => setDrawerOpen(true)}
                aria-label="Open menu"
                className={`${roundButton} lg:hidden`}
              >
                <Menu size={20} />
              </button>
              <div className="min-w-0 flex-1">
                {topLeading ??
                  (context && (
                    <p className="truncate px-1 text-sm font-medium text-gray-600 dark:text-gray-300">
                      {context}
                    </p>
                  ))}
              </div>
              <button
                type="button"
                onClick={toggleTheme}
                aria-label="Toggle theme"
                className={`${roundButton} hidden sm:inline-flex`}
              >
                {theme === 'light' ? <Moon size={19} /> : <Sun size={19} />}
              </button>
              <NotificationCenter buttonClassName={roundButton} />
              <div className="flex min-w-0 items-center gap-3 pl-1">
                <span
                  aria-hidden="true"
                  className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-gradient-to-br from-primary-400 to-primary-700 text-sm font-semibold text-white"
                >
                  {initials(user.name, user.email)}
                </span>
                <div className="hidden min-w-0 xl:block">
                  <p className="truncate text-sm font-semibold text-gray-900 dark:text-white">
                    {user.name || user.email}
                  </p>
                  {user.name && (
                    <p className="truncate text-xs text-gray-500 dark:text-gray-400">
                      {user.email}
                    </p>
                  )}
                </div>
              </div>
            </header>

            <main
              ref={setScrollArea}
              className="min-h-0 flex-1 overflow-y-auto rounded-[28px] bg-panel p-4 dark:bg-gray-900 sm:p-6 xl:p-8"
            >
              <div className="mx-auto max-w-[1600px]">{children}</div>
            </main>
          </div>
        </div>
      </ScrollAreaContext.Provider>
    </CloseNavContext.Provider>
  );
}

/** Large page heading with an optional subtitle and actions on the right. */
export function PageTitle({
  title,
  subtitle,
  actions,
}: {
  title: string;
  subtitle?: ReactNode;
  actions?: ReactNode;
}) {
  return (
    <div className="mb-6 flex flex-wrap items-end justify-between gap-4">
      <div className="min-w-0">
        <h1 className="text-3xl font-semibold tracking-tight text-gray-900 dark:text-white sm:text-4xl">
          {title}
        </h1>
        {subtitle && (
          <p className="mt-2 text-sm text-gray-500 dark:text-gray-400 sm:text-base">
            {subtitle}
          </p>
        )}
      </div>
      {actions && <div className="flex flex-wrap items-center gap-3">{actions}</div>}
    </div>
  );
}
